"use client"

import * as React from "react"
import { useTheme } from "next-themes"
import { Toaster as Sonner, ToasterProps } from "sonner"

/**
 * Read a toast the way it appears on screen.
 *
 * Sonner marks its own parts, so prefer those and fall back to the raw text if
 * the markup ever changes. Buttons inside the toast are skipped: they are
 * reachable in their own right and would otherwise be read as part of the
 * sentence.
 */
function readToast(el: Element): string {
  const title = el.querySelector("[data-title]")?.textContent?.trim()
  const description = el.querySelector("[data-description]")?.textContent?.trim()

  if (title || description) {
    return [title, description].filter(Boolean).join(". ")
  }

  const clone = el.cloneNode(true) as Element
  clone.querySelectorAll("button").forEach((b) => b.remove())
  return (clone.textContent ?? "").replace(/\s+/g, " ").trim()
}

/**
 * Speaks every toast.
 *
 * Sonner does wrap its toasts in an aria-live region and that region genuinely
 * exists before a toast arrives — the markup is fine. The problem is that it is
 * "polite": those announcements are queued and dropped whenever the screen
 * reader is already busy, which is exactly the moment a toast usually fires.
 * Submitting a post closes a dialog, moves focus and re-renders the feed all at
 * once, so the confirmation never gets its turn.
 *
 * This watches the DOM rather than sonner's React state on purpose. State only
 * exposes what was passed in, so a title built from JSX reads as nothing, and a
 * toast.loading() that is later updated in place keeps its id and would be
 * treated as already announced — the user would hear "uploading" and never the
 * result. Whatever ends up on screen gets spoken, however it was created.
 *
 * Sonner's own region is muted so nothing is said twice; its section props are
 * hardcoded with no spread, so that has to be done on the node.
 */

/** Quiet gap after the last focus move before it is safe to speak. */
const FOCUS_SETTLE_MS = 400
/** Never hold an announcement longer than this, however restless focus is. */
const MAX_HOLD_MS = 2500
/** How long an injected announcement stays in the DOM before cleanup. */
const ANNOUNCEMENT_TTL_MS = 5000

/**
 * Speaks every toast.
 *
 * Sonner wraps its toasts in an aria-live region, but a polite one: those
 * announcements are queued and dropped whenever the screen reader is busy,
 * which is exactly when a toast fires. Submitting a post closes a dialog,
 * restores focus and re-renders the feed all at once.
 *
 * Two things make this work where a plain React-rendered live region did not.
 *
 * The announcement is injected as a fresh role="alert" element straight into
 * <body>, not rendered by React. A region living inside the provider tree is
 * being reconciled by React at the very moment router.refresh() re-renders the
 * feed, and the insertion gets lost in that churn. Appending outside the tree
 * puts it somewhere nothing else is touching.
 *
 * And it waits for focus to stop moving first. Whatever gets focused is
 * announced last and talks over anything already speaking, so announcing during
 * the hand-off means the confirmation is cut off by the button that took focus.
 */
function ToastAnnouncer() {
  React.useEffect(() => {
    const spoken = new WeakMap<Element, string>()
    let queued = 0
    let pending: string | null = null
    let holdTimer: number | null = null
    let deadline = 0

    const speak = () => {
      holdTimer = null
      deadline = 0
      const text = pending
      pending = null
      if (!text) return

      const node = document.createElement("div")
      node.setAttribute("role", "alert")
      node.setAttribute("aria-live", "assertive")
      node.setAttribute("data-toast-announcement", "")
      node.className = "sr-only"
      node.textContent = text
      document.body.appendChild(node)
      window.setTimeout(() => node.remove(), ANNOUNCEMENT_TTL_MS)
    }

    const hold = () => {
      if (!pending) return
      if (holdTimer) window.clearTimeout(holdTimer)
      const now = Date.now()
      if (!deadline) deadline = now + MAX_HOLD_MS
      holdTimer = window.setTimeout(
        speak,
        Math.max(0, Math.min(FOCUS_SETTLE_MS, deadline - now))
      )
    }

    const onFocusMove = () => hold()
    document.addEventListener("focusin", onFocusMove, true)
    document.addEventListener("focusout", onFocusMove, true)

    const sweep = () => {
      queued = 0
      document
        .querySelector('section[aria-live="polite"][aria-label^="Notifications"]')
        ?.setAttribute("aria-live", "off")

      const fresh: string[] = []
      document.querySelectorAll("[data-sonner-toast]").forEach((el) => {
        const text = readToast(el)
        if (!text || spoken.get(el) === text) return
        spoken.set(el, text)
        fresh.push(text)
      })
      if (!fresh.length) return

      pending = fresh.join(". ")
      hold()
    }

    const observer = new MutationObserver((records) => {
      // Skip our own injected nodes, or announcing would retrigger the sweep.
      if (
        records.every((r) => {
          const t = r.target as HTMLElement
          return t?.closest?.("[data-toast-announcement]")
        })
      ) {
        return
      }
      if (queued) return
      queued = requestAnimationFrame(sweep)
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    })
    sweep()

    return () => {
      observer.disconnect()
      document.removeEventListener("focusin", onFocusMove, true)
      document.removeEventListener("focusout", onFocusMove, true)
      if (queued) cancelAnimationFrame(queued)
      if (holdTimer) window.clearTimeout(holdTimer)
      document
        .querySelectorAll("[data-toast-announcement]")
        .forEach((n) => n.remove())
    }
  }, [])

  return null
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  return (
    <>
      <Sonner
        theme={theme as ToasterProps["theme"]}
        className="toaster group"
        style={
          {
            "--normal-bg": "var(--popover)",
            "--normal-text": "var(--popover-foreground)",
            "--normal-border": "var(--border)",
          } as React.CSSProperties
        }
        {...props}
      />
      <ToastAnnouncer />
    </>
  )
}

export { Toaster }
